import { Box, Button, Paper, Typography } from '@mui/material'
import { format, subDays } from 'date-fns'
import { useEffect, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { usePageViewTracking } from 'src/hooks/use-page-view-tracking'
import { useGuidedToursList } from '~components/guided-tour/content/guided-tours-list'
import { startGuidedTour } from '~components/guided-tour/hooks/start-guided-tour'
import { Splash } from '~components/page-header/splash'
import { PageWrapper } from '~components/page-wrapper'
import { useGetPortInfo } from '~hooks/queries/port-info/use-get-port-info'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { useScrollHint } from '~hooks/use-scroll-hint'
import { COLORS } from '~theme/colors'
import { AddVisitFlow } from './components/add-visit/add-visit-flow'
import { EntryDetail } from './components/entry-detail'
import { FilterBar } from './components/filter-bar'
import { PortList } from './components/port-list'
import { TerminalDetail } from './components/terminal-detail'
import { usePortInfoNavigation } from './hooks/use-port-info-navigation'
import { groupPortInfoByCategory } from './utils/group-port-info'

const PORT_INFO_USER_GUIDE_URL =
  'https://strmtnoeupvesseltruth01.blob.core.windows.net/public/InSite/Port%20Info%20-%20User%20guide.pdf'

export const addVisitSearchParam = 'addVisit'

export default function PortInfoPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const isAddVisitModalOpen = searchParams.get(addVisitSearchParam) === 'true'
  const panelsContainerRef = useRef<HTMLDivElement>(null)
  useScrollHint({ trigger: true, ref: panelsContainerRef })
  usePageViewTracking('Port Info')
  const canWritePortInfo = useHasScope(
    ExactScopeName.technicalManagement,
    ExactScopeName.portInfoWrite,
  )
  const portInfoTour = useGuidedToursList().find((tour) =>
    tour.tourPaths.includes('port-info'),
  )
  const showGuidedTourButton = Boolean(portInfoTour)

  const {
    expandedCategories,
    activeCategory,
    selectedPortName,
    selectedTerminal,
    selectedBerthName,
    selectedContinentCode,
    selectedCountryCode,
    selectedEntryId,
    handleSelectCategory,
    handleSelectPort,
    handleSelectTerminal,
    handleSelectBerth,
    handleSelectContinent,
    handleSelectCountry,
    handleSelectEntry,
  } = usePortInfoNavigation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on initial mount to set default category
  useEffect(() => {
    const hasCategory = searchParams.getAll('category').length > 0
    if (!hasCategory) {
      setSearchParams(
        (prev) => {
          prev.append('category', 'ports')
          prev.set('activeCategory', 'ports')
          return prev
        },
        { replace: true },
      )
    }
  }, [])

  const lastVisitParam = searchParams.get('lastVisit')

  const dateOfArrival = useMemo(() => {
    if (lastVisitParam && lastVisitParam !== '') {
      const days = parseInt(lastVisitParam, 10)
      if (!Number.isFinite(days)) {
        return undefined
      }
      return format(subDays(new Date(), days), 'yyyy-MM-dd')
    }
    return undefined
  }, [lastVisitParam])

  const filterParams = {
    portCountry: searchParams.getAll('portCountry'),
    portName: searchParams.getAll('portName'),
    terminalName: searchParams.getAll('terminalName'),
    berthName: searchParams.getAll('berthName'),
    dateOfArrival,
  }

  const { data: portInfoItems, isLoading } = useGetPortInfo(filterParams)

  const groupedData = useMemo(
    () => groupPortInfoByCategory(portInfoItems ?? []),
    [portInfoItems],
  )

  const selectedPortData = groupedData.ports.find(
    (p) => p.portName === selectedPortName,
  )
  const selectedTerminalData = selectedPortData?.terminals.find(
    (t) => t.terminalName === selectedTerminal,
  )

  function setAddVisitModalOpen(isOpen: boolean) {
    setSearchParams((prev) => {
      if (isOpen) prev.set(addVisitSearchParam, 'true')
      else prev.delete(addVisitSearchParam)
      return prev
    })
  }

  return (
    <>
      <Helmet>
        <title>InSite | Port Info</title>
      </Helmet>
      <Splash pageName="Port Info" omitPoolSelector />
      <PageWrapper>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="h3">
            Find information from past visits
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {showGuidedTourButton && portInfoTour && (
              <Button
                onClick={() => startGuidedTour(portInfoTour)}
                sx={{ color: COLORS.secondary.barBlue }}
              >
                Guided Tour
              </Button>
            )}
            <Button
              component="a"
              href={PORT_INFO_USER_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: COLORS.secondary.barBlue }}
            >
              User Guide
            </Button>
            {canWritePortInfo && (
              <Button
                className="tour-step-port-info-add-visit"
                variant="contained"
                onClick={() => setAddVisitModalOpen(true)}
              >
                Add Visit
              </Button>
            )}
          </Box>
        </Box>

        <FilterBar />

        <Box
          ref={panelsContainerRef}
          sx={{
            display: 'flex',
            gap: 2,
            mt: 2,
            minHeight: 'calc(100vh - 280px)',
            overflowX: 'auto',
          }}
        >
          <Paper
            className="tour-step-port-info-nav-panel"
            elevation={0}
            sx={{
              width: '30%',
              minWidth: 300,
              overflow: 'auto',
              bgcolor: COLORS.primary.white,
              borderRadius: 1,
              border: 1,
              borderColor: COLORS.greys.light,
            }}
          >
            <PortList
              groupedData={groupedData}
              isLoading={isLoading}
              expandedCategories={expandedCategories}
              activeCategory={activeCategory}
              selectedPortName={selectedPortName}
              selectedTerminal={selectedTerminal}
              selectedContinentCode={selectedContinentCode}
              selectedCountryCode={selectedCountryCode}
              selectedEntryId={selectedEntryId}
              onSelectCategory={handleSelectCategory}
              onSelectPort={handleSelectPort}
              onSelectTerminal={handleSelectTerminal}
              onSelectContinent={handleSelectContinent}
              onSelectCountry={handleSelectCountry}
              onSelectEntry={handleSelectEntry}
            />
          </Paper>

          <Paper
            className="tour-step-port-info-detail-panel"
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 400,
              overflow: 'auto',
              bgcolor: COLORS.primary.white,
              borderRadius: 1,
              border: 1,
              borderColor: COLORS.greys.light,
              p: selectedEntryId ? 2 : 0,
            }}
          >
            {selectedEntryId ? (
              <EntryDetail entryId={selectedEntryId} />
            ) : (
              <TerminalDetail
                port={selectedPortData}
                terminal={selectedTerminalData}
                selectedBerthName={selectedBerthName}
                onSelectBerth={handleSelectBerth}
              />
            )}
          </Paper>
        </Box>
      </PageWrapper>

      {isAddVisitModalOpen && (
        <AddVisitFlow onClose={() => setAddVisitModalOpen(false)} />
      )}
    </>
  )
}
